// src/features/landing/SaveAsTemplateDialog.tsx
// Save the current page's sections + theme as a reusable template.
// CreateLandingTemplate. Reached from the page editor, not the template
// gallery — a template needs a source page to build from.

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Input, Textarea, Button, Alert } from '@react/components';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { landingApi } from '@core/api';
import type { LandingPage, LandingTemplate } from '@core/types/landing';
import { LandingModal } from './Modal';
import { errMessage, makeEntity, type ScopeIds } from './helpers';

interface SaveAsTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  page: LandingPage;
  scope: ScopeIds;
  onSaved: (template: LandingTemplate) => void;
}

export function SaveAsTemplateDialog({ open, onClose, page, scope, onSaved }: SaveAsTemplateDialogProps) {
  const { t } = useI18n();
  const [name, setName] = useState(page.title);
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!name.trim()) {
      setError(t('landing.templates.nameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const pageSchema = JSON.stringify({ sections: page.sections, theme: page.theme });
      const res = await landingApi.CreateLandingTemplate({
        entity: makeEntity('', 'landing_template', scope),
        name: name.trim(),
        description,
        category: category || undefined,
        page_schema: pageSchema,
        is_public: isPublic,
        preview_image_url: previewImageUrl || undefined,
      });
      if (res.template) onSaved(res.template);
      onClose();
    } catch (e) {
      setError(errMessage(e, t('landing.templates.saveError')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <LandingModal
      open={open}
      onClose={onClose}
      title={t('landing.templates.saveAsTemplate')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="error" className="mb-3">
          {error}
        </Alert>
      )}
      <div className="space-y-3">
        <Input label={t('landing.templates.name')} value={name} onChange={(e) => setName(e.target.value)} required />
        <Textarea label={t('landing.templates.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        <Input label={t('landing.templates.category')} value={category} onChange={(e) => setCategory(e.target.value)} placeholder="marketing" />
        <Input label={t('landing.templates.previewImage')} value={previewImageUrl} onChange={(e) => setPreviewImageUrl(e.target.value)} />
        <ToggleSwitch label={t('landing.templates.isPublic')} description={t('landing.templates.isPublicHelp')} checked={isPublic} onChange={setIsPublic} />
      </div>
    </LandingModal>
  );
}
